import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  FormControl,
  FormControlLabel,
  InputLabel,
  Link,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  SelectChangeEvent,
  Stack,
  Typography,
  Paper,
} from '@mui/material';
import type { Pool } from '../../types/domain';
import CameraLayoutSimulator, {
  type LayoutHydrateInfo,
} from '../../components/pool/CameraLayoutSimulator';
import DraftNumberField from '../../components/common/DraftNumberField';
import { openPoolManagementPopupWindow } from '../../components/monitoring/cctvPopupRoutes';
import {
  proposeCameraSpec,
  proposalToSeed,
  recommendInstallHeightM,
  type CameraSpecProposal,
  type SpecMountMode,
  type SpecLensMode,
  type CameraSpecSeed,
} from '../../utils/cameraSpecWizard';
import { DEFAULT_FOCAL_LENGTH_MM } from '../../utils/cameraOptics';

const STORAGE_KEY = 'kyma.cameraSpecWizard.poolId';
const DRAFT_KEY = 'kyma.cameraSpecWizard.draft';

type WizardDraft = {
  poolId: number;
  lengthM: number;
  widthM: number;
  mountMode: SpecMountMode;
  lensMode: SpecLensMode;
  focalLengthMm: number;
};

function readDraft(poolId: number): WizardDraft | null {
  try {
    const raw = sessionStorage.getItem(DRAFT_KEY);
    if (!raw) return null;
    const d = JSON.parse(raw) as WizardDraft;
    if (Number(d.poolId) !== poolId) return null;
    return d;
  } catch {
    return null;
  }
}

function writeDraft(d: WizardDraft) {
  try {
    sessionStorage.setItem(DRAFT_KEY, JSON.stringify(d));
  } catch {
    /* ignore */
  }
}

function comparePoolCode(a: string, b: string): number {
  return a.localeCompare(b, 'en', { numeric: true, sensitivity: 'base' });
}

function poolOptionLabel(pool: Pool): string {
  const code = String(pool.code || '').trim();
  const name = String(pool.name || '').trim();
  if (code && name) return `${code} (${name})`;
  return code || name || '—';
}

type Props = {
  pools: Pool[];
  loading?: boolean;
};

/**
 * 시방 자동 배치 — 천장/벽면 + L/W만 입력. H·대수·각도·화각은 시방 표 자동.
 */
const CameraSpecWizardPage: React.FC<Props> = ({ pools, loading = false }) => {
  const [poolId, setPoolId] = useState<number | ''>('');
  const [lengthM, setLengthM] = useState(25);
  const [widthM, setWidthM] = useState(12);
  const [mountMode, setMountMode] = useState<SpecMountMode>('ceiling');
  const [lensMode, setLensMode] = useState<SpecLensMode>('fixed');
  const [focalLengthMm, setFocalLengthMm] = useState(DEFAULT_FOCAL_LENGTH_MM);
  const [proposal, setProposal] = useState<CameraSpecProposal | null>(null);
  const [seed, setSeed] = useState<CameraSpecSeed | null>(null);
  const [seedToken, setSeedToken] = useState(0);

  const previewH = useMemo(
    () => recommendInstallHeightM(lengthM, widthM, mountMode),
    [lengthM, widthM, mountMode],
  );

  const sortedPools = useMemo(
    () =>
      [...pools].sort((a, b) =>
        comparePoolCode(String(a.code || ''), String(b.code || '')),
      ),
    [pools],
  );

  useEffect(() => {
    if (!sortedPools.length) {
      setPoolId('');
      return;
    }
    const ids = sortedPools.map((p) => Number(p.id)).filter((id) => !Number.isNaN(id));
    const stored = sessionStorage.getItem(STORAGE_KEY);
    const storedNum = stored ? Number(stored) : NaN;
    if (stored && ids.includes(storedNum)) {
      setPoolId(storedNum);
      return;
    }
    const first = Number(sortedPools[0].id);
    if (!Number.isNaN(first)) setPoolId(first);
  }, [sortedPools]);

  const handlePoolChange = (e: SelectChangeEvent<number | ''>) => {
    const v = e.target.value;
    if (v === '') {
      setPoolId('');
      sessionStorage.removeItem(STORAGE_KEY);
      return;
    }
    const n = Number(v);
    setPoolId(n);
    sessionStorage.setItem(STORAGE_KEY, String(n));
    const draft = readDraft(n);
    if (draft) {
      setLengthM(draft.lengthM);
      setWidthM(draft.widthM);
      setMountMode(draft.mountMode);
      setLensMode(draft.lensMode);
      setFocalLengthMm(draft.focalLengthMm);
      setProposal(null);
    }
  };

  /** 저장·불러오기 후 시방 입력란을 시뮬레이터/서버 값과 맞춤 */
  const handleLayoutHydrated = useCallback((info: LayoutHydrateInfo) => {
    setLengthM(info.poolLengthM);
    setWidthM(info.poolWidthM);
    setMountMode(info.mountMode);
    setLensMode(info.lensFixed ? 'fixed' : 'variable');
    setFocalLengthMm(info.focalLengthMm);
    if (poolId !== '' && !Number.isNaN(Number(poolId))) {
      writeDraft({
        poolId: Number(poolId),
        lengthM: info.poolLengthM,
        widthM: info.poolWidthM,
        mountMode: info.mountMode,
        lensMode: info.lensFixed ? 'fixed' : 'variable',
        focalLengthMm: info.focalLengthMm,
      });
    }
  }, [poolId]);

  useEffect(() => {
    if (poolId === '' || Number.isNaN(Number(poolId))) return;
    writeDraft({
      poolId: Number(poolId),
      lengthM,
      widthM,
      mountMode,
      lensMode,
      focalLengthMm,
    });
  }, [poolId, lengthM, widthM, mountMode, lensMode, focalLengthMm]);

  useEffect(() => {
    if (poolId === '' || Number.isNaN(Number(poolId))) return;
    const draft = readDraft(Number(poolId));
    if (!draft) return;
    setLengthM(draft.lengthM);
    setWidthM(draft.widthM);
    setMountMode(draft.mountMode);
    setLensMode(draft.lensMode);
    setFocalLengthMm(draft.focalLengthMm);
  }, [poolId]);

  const selectedPool = sortedPools.find((p) => Number(p.id) === poolId);

  const proposeArgs = () => ({
    lengthM,
    widthM,
    mountMode,
    lensMode,
    focalLengthMm,
  });

  const runPropose = () => {
    const p = proposeCameraSpec(proposeArgs());
    setProposal(p);
    if (poolId !== '' && !Number.isNaN(Number(poolId))) {
      setSeed(proposalToSeed(p));
      setSeedToken((t) => t + 1);
      writeDraft({
        poolId: Number(poolId),
        lengthM: p.lengthM,
        widthM: p.widthM,
        mountMode: p.mountMode,
        lensMode: p.lensMode,
        focalLengthMm: p.focalLengthMm,
      });
    }
  };

  const applyToSimulator = () => {
    const p = proposal || proposeCameraSpec(proposeArgs());
    setProposal(p);
    setSeed(proposalToSeed(p));
    setSeedToken((t) => t + 1);
    if (poolId !== '' && !Number.isNaN(Number(poolId))) {
      writeDraft({
        poolId: Number(poolId),
        lengthM: p.lengthM,
        widthM: p.widthM,
        mountMode: p.mountMode,
        lensMode: p.lensMode,
        focalLengthMm: p.focalLengthMm,
      });
    }
  };

  return (
    <Box>
      <Typography variant="h5" sx={{ mb: 1, fontWeight: 600 }}>
        카메라 시방 자동 배치
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        천장/벽면·L/W와 <strong>렌즈 고정/가변</strong>을 넣으면 시방 표로 설치 높이·대수·하향각·화각
        산정. 고정 렌즈는 초점(mm) 고정, 가변은 자동 배치 시 줌 조정 가능.
      </Typography>

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }} sx={{ mb: 2 }}>
        <FormControl size="small" sx={{ minWidth: 280 }} disabled={loading || !sortedPools.length}>
          <InputLabel id="spec-wizard-pool-label">작업 시설</InputLabel>
          <Select
            labelId="spec-wizard-pool-label"
            label="작업 시설"
            value={poolId}
            onChange={handlePoolChange}
          >
            {sortedPools.map((pool) => {
              const id = Number(pool.id);
              if (Number.isNaN(id)) return null;
              return (
                <MenuItem key={id} value={id}>
                  {poolOptionLabel(pool)}
                </MenuItem>
              );
            })}
          </Select>
        </FormControl>
        {selectedPool && (
          <Chip
            size="small"
            variant="outlined"
            label={`저장 대상: ${selectedPool.code || selectedPool.name}`}
          />
        )}
        {selectedPool?.code && (
          <Link
            component="button"
            type="button"
            variant="body2"
            onClick={() => openPoolManagementPopupWindow(selectedPool.code)}
            sx={{ ml: { sm: 'auto' }, cursor: 'pointer', border: 'none', background: 'none' }}
          >
            시설 관리
          </Link>
        )}
      </Stack>

      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle2" sx={{ mb: 1.5 }}>
          입력 (설치 모드 + L / W) — H는 자동
        </Typography>

        <Typography variant="body2" sx={{ mb: 0.5 }}>
          설치 모드
        </Typography>
        <RadioGroup
          row
          value={mountMode}
          onChange={(_, v) => {
            setMountMode(v as SpecMountMode);
            setProposal(null);
          }}
          sx={{ mb: 2 }}
        >
          <FormControlLabel value="ceiling" control={<Radio size="small" />} label="천장 (모드 C)" />
          <FormControlLabel value="wall" control={<Radio size="small" />} label="벽면 (모드 W)" />
        </RadioGroup>

        <Typography variant="body2" sx={{ mb: 0.5 }}>
          렌즈
        </Typography>
        <RadioGroup
          row
          value={lensMode}
          onChange={(_, v) => {
            setLensMode(v as SpecLensMode);
            setProposal(null);
          }}
          sx={{ mb: 1 }}
        >
          <FormControlLabel
            value="fixed"
            control={<Radio size="small" />}
            label="고정 초점 (H/V 잠금)"
          />
          <FormControlLabel
            value="variable"
            control={<Radio size="small" />}
            label="가변(광학 줌)"
          />
        </RadioGroup>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2 }} alignItems={{ sm: 'flex-start' }}>
          <DraftNumberField
            size="small"
            label={lensMode === 'fixed' ? '고정 초점 (mm)' : '기준 줌 (mm)'}
            value={focalLengthMm}
            onValueChange={(v) => {
              setFocalLengthMm(v);
              setProposal(null);
            }}
            min={1.8}
            max={16}
            emptyFallback={DEFAULT_FOCAL_LENGTH_MM}
            inputProps={{ min: 1.8, max: 16, step: 0.1 }}
            helperText={
              lensMode === 'fixed'
                ? `유통·시방 권장 ${DEFAULT_FOCAL_LENGTH_MM} mm. 고정 시 자동 배치가 화각을 바꾸지 않음`
                : `시작 줌(기본 ${DEFAULT_FOCAL_LENGTH_MM} mm). 자동 배치가 커버에 맞춰 조정 가능`
            }
            sx={{ minWidth: 200 }}
          />
          {focalLengthMm !== DEFAULT_FOCAL_LENGTH_MM && (
            <Button
              size="small"
              variant="text"
              onClick={() => {
                setFocalLengthMm(DEFAULT_FOCAL_LENGTH_MM);
                setProposal(null);
              }}
              sx={{ mt: { sm: 0.5 } }}
            >
              {DEFAULT_FOCAL_LENGTH_MM} mm로
            </Button>
          )}
        </Stack>

        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mb: 2 }} alignItems={{ md: 'flex-start' }}>
          <DraftNumberField
            size="small"
            label="L 가로·장변 (m)"
            value={lengthM}
            onValueChange={(v) => {
              setLengthM(v);
              setProposal(null);
            }}
            min={3}
            max={120}
            emptyFallback={25}
            inputProps={{ min: 3, max: 120, step: 0.1 }}
            helperText="Length — 레인 방향 길이"
            sx={{ minWidth: 160 }}
          />
          <DraftNumberField
            size="small"
            label="W 세로·폭 (m)"
            value={widthM}
            onValueChange={(v) => {
              setWidthM(v);
              setProposal(null);
            }}
            min={2}
            max={80}
            emptyFallback={12}
            inputProps={{ min: 2, max: 80, step: 0.1 }}
            helperText="Width — 수면 폭"
            sx={{ minWidth: 160 }}
          />
          <Chip
            color="info"
            variant="outlined"
            label={`자동 H ≈ ${previewH} m (${mountMode === 'ceiling' ? '천장' : '벽면'} 시방 대표)`}
            sx={{ mt: { md: 0.5 } }}
          />
        </Stack>

        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          <Button variant="outlined" onClick={runPropose}>
            시방 제안 계산
          </Button>
          <Button
            variant="contained"
            onClick={applyToSimulator}
            disabled={poolId === '' || Number.isNaN(Number(poolId))}
          >
            제안 → 시뮬레이터 자동 배치
          </Button>
        </Stack>

        {proposal && (
          <Box sx={{ mt: 2 }}>
            <Divider sx={{ mb: 1.5 }} />
            <Typography variant="subtitle2" gutterBottom>
              제안 결과 ({proposal.areaBandLabel})
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
              <Chip
                size="small"
                color="primary"
                label={`H ${proposal.heightM} m${proposal.heightAuto ? ' (자동)' : ''}`}
              />
              <Chip size="small" color="primary" label={`대수 ${proposal.cameraCount}`} />
              <Chip
                size="small"
                label={
                  proposal.mountMode === 'ceiling'
                    ? `격자 ${proposal.ceilingRows}×${proposal.ceilingCols}`
                    : `벽 ${proposal.wallRows}×${proposal.wallCols} +좌${proposal.wallLeftCount}/우${proposal.wallRightCount}`
                }
              />
              <Chip
                size="small"
                label={
                  proposal.mountMode === 'ceiling'
                    ? `하향 ${proposal.ceilingTiltDeg}°`
                    : `하향 ${proposal.wallTiltDeg}°`
                }
              />
              <Chip
                size="small"
                label={
                  proposal.lensMode === 'fixed'
                    ? `렌즈 고정 ${proposal.focalLengthMm} mm`
                    : `렌즈 가변 기준 ${proposal.focalLengthMm} mm`
                }
              />
              <Chip
                size="small"
                label={`화각 H${proposal.fovHDeg}°/V${proposal.fovVDeg}°`}
              />
              <Chip size="small" variant="outlined" label={`A=${proposal.areaM2.toFixed(0)} m²`} />
            </Stack>
            {proposal.rationale.map((line) => (
              <Typography key={line} variant="caption" display="block" color="text.secondary">
                · {line}
              </Typography>
            ))}
            <Typography variant="caption" display="block" sx={{ mt: 1 }} color="text.secondary">
              아래 도면에서 번호 마커·하단 자동 계산값 행을 클릭하면 해당 카메라 화각·커버리지
              강조. 방위(heading)는 자동 배치 시 수면 방향으로 산출.
            </Typography>
          </Box>
        )}
      </Paper>

      {loading && (
        <Alert severity="info" sx={{ mb: 2 }}>
          시설 목록을 불러오는 중…
        </Alert>
      )}
      {!loading && !sortedPools.length && (
        <Alert severity="warning">등록된 시설 없음.</Alert>
      )}

      {poolId !== '' && !Number.isNaN(Number(poolId)) && (
        <CameraLayoutSimulator
          key={poolId}
          poolId={Number(poolId)}
          poolName={selectedPool?.name}
          specSeed={seed}
          specSeedToken={seedToken}
          onLayoutHydrated={handleLayoutHydrated}
        />
      )}
    </Box>
  );
};

export default CameraSpecWizardPage;
